import { newId, type ApiError, type CaptureResult } from '@zibanu/shared';
import { OfflineError, type ZibanuClient } from './client';
import type { BlobLike, StorageLike } from './types';

/** What waits on the phone to be sent. */
export interface QueuedCapture {
  /** The idempotency key: made once, carried by every retry. */
  key: string;
  orgId: string;
  createdAt: string;
  kind: 'text' | 'tap' | 'media';
  text?: string | undefined;
  file?: BlobLike | undefined;
  fileName?: string | undefined;
  caption?: string | undefined;
  attempts: number;
  nextTryAt: string;
}

export interface FailedCapture extends QueuedCapture {
  error: ApiError;
  reason?: string | undefined;
  failedAt: string;
}

/** Sent by a background flush: its reply waits for the person to see it. */
export interface SentCapture {
  key: string;
  orgId: string;
  kind: QueuedCapture['kind'];
  text?: string | undefined;
  caption?: string | undefined;
  /** voice.webm / voice.m4a for a voice note: the sheet shows "Voice note" rather than "Photo". */
  fileName?: string | undefined;
  sentAt: string;
  replies: CaptureResult['replies'];
  /** The server already had it (the answer to an earlier try was lost). */
  duplicate: boolean;
}

export type SendOutcome =
  | { status: 'sent'; replies: CaptureResult['replies']; duplicate: boolean }
  | { status: 'queued' }
  | { status: 'failed'; error: ApiError; reason?: string | undefined }
  | { status: 'signed_out' };

export type NewCapture = { orgId: string } & (
  | { kind: 'text' | 'tap'; text: string }
  | { kind: 'media'; file: BlobLike; fileName: string; caption?: string | undefined }
);

export interface Outbox {
  add(item: NewCapture): Promise<QueuedCapture>;
  /** Try one now (the capture sheet). Sent: gone from the phone, reply returned, nothing kept in "sent". */
  sendNow(key: string): Promise<SendOutcome>;
  /** Everything due, oldest first, one at a time; a dropped connection stops it. */
  flush(opts?: { force?: boolean }): Promise<void>;
  queued(orgId?: string): Promise<QueuedCapture[]>;
  failed(orgId?: string): Promise<FailedCapture[]>;
  sent(orgId?: string): Promise<SentCapture[]>;
  retry(key: string): Promise<void>;
  discard(key: string): Promise<void>;
  markSeen(key: string): Promise<void>;
  clearOrg(orgId: string): Promise<void>;
  clearAll(): Promise<void>;
  subscribe(listener: () => void): () => void;
}

/** 30 s, 60 s, 120 s, 240 s, then every 5 minutes. */
export const RETRY_DELAYS_MS = [30_000, 60_000, 120_000, 240_000, 300_000] as const;

const Q = 'outbox:q:';
const F = 'outbox:f:';
const S = 'outbox:s:';

export function createOutbox(opts: {
  client: ZibanuClient;
  storage: StorageLike;
  now?: () => Date;
  newKey?: () => string;
}): Outbox {
  const { client, storage } = opts;
  const now = opts.now ?? (() => new Date());
  const newKey = opts.newKey ?? newId;
  const listeners = new Set<() => void>();
  const changed = () => {
    for (const l of [...listeners]) l();
  };

  // One lock for every send: an immediate send and a flush never send the same capture twice.
  let lock: Promise<unknown> = Promise.resolve();
  const exclusive = <T>(work: () => Promise<T>): Promise<T> => {
    const run = lock.then(work, work);
    lock = run.catch(() => undefined);
    return run;
  };

  const list = async <T>(prefix: string, orgId?: string): Promise<T[]> => {
    const items: T[] = [];
    for (const k of (await storage.keys(prefix)).sort()) {
      const item = await storage.get<T & { orgId: string }>(k);
      if (item && (!orgId || item.orgId === orgId)) items.push(item);
    }
    return items;
  };

  const reschedule = async (item: QueuedCapture) => {
    const attempts = item.attempts + 1;
    const delay = RETRY_DELAYS_MS[Math.min(attempts, RETRY_DELAYS_MS.length) - 1]!;
    await storage.set(Q + item.key, { ...item, attempts, nextTryAt: new Date(now().getTime() + delay).toISOString() });
  };

  /** One try. Callers hold the lock. */
  const attempt = async (key: string, keepReply: boolean): Promise<SendOutcome> => {
    const item = await storage.get<QueuedCapture>(Q + key);
    // Gone while this one waited for the lock: callers that care use resolveGone.
    if (!item) return { status: 'sent', replies: [], duplicate: true };
    let res: Awaited<ReturnType<ZibanuClient['capture']['text']>>;
    try {
      res =
        item.kind === 'media'
          ? await client.capture.media(item.key, item.file!, item.fileName!, item.caption, item.orgId)
          : item.kind === 'tap'
            ? await client.capture.quickReply(item.key, item.text!, item.orgId)
            : await client.capture.text(item.key, item.text!, item.orgId);
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      if (!(await storage.get(Q + key))) return { status: 'queued' }; // wiped meanwhile: write nothing
      await reschedule(item);
      return { status: 'queued' };
    }
    // Wiped (discarded, or the books cleared) while the request was out: write nothing back.
    if (!(await storage.get(Q + key))) return { status: 'queued' };
    if (res.ok) {
      if (keepReply) {
        const sent: SentCapture = {
          key,
          orgId: item.orgId,
          kind: item.kind,
          text: item.text,
          caption: item.caption,
          fileName: item.fileName,
          sentAt: now().toISOString(),
          replies: res.data.replies,
          duplicate: res.data.duplicate,
        };
        await storage.set(S + key, sent);
      }
      await storage.delete(Q + key);
      return { status: 'sent', replies: res.data.replies, duplicate: res.data.duplicate };
    }
    if (res.error === 'unauthenticated') return { status: 'signed_out' };
    if (res.error === 'internal' || res.error === 'rate_limited') {
      await reschedule(item);
      return { status: 'queued' };
    }
    const failed: FailedCapture = { ...item, error: res.error, reason: res.reason, failedAt: now().toISOString() };
    await storage.set(F + key, failed);
    await storage.delete(Q + key);
    return { status: 'failed', error: res.error, ...(res.reason ? { reason: res.reason } : {}) };
  };

  /** The capture left the queue before this path's turn: say what became of it. Callers hold the lock. */
  const resolveGone = async (key: string): Promise<SendOutcome> => {
    const failed = await storage.get<FailedCapture>(F + key);
    if (failed) return { status: 'failed', error: failed.error, ...(failed.reason ? { reason: failed.reason } : {}) };
    const sent = await storage.get<SentCapture>(S + key);
    if (sent) {
      await storage.delete(S + key); // shown once, here
      return { status: 'sent', replies: sent.replies, duplicate: sent.duplicate };
    }
    return { status: 'sent', replies: [], duplicate: true };
  };

  return {
    async add(input) {
      const at = now().toISOString();
      const item: QueuedCapture = {
        key: newKey(),
        orgId: input.orgId,
        createdAt: at,
        kind: input.kind,
        ...(input.kind === 'media'
          ? { file: input.file, fileName: input.fileName, caption: input.caption }
          : { text: input.text }),
        attempts: 0,
        nextTryAt: at,
      };
      await storage.set(Q + item.key, item);
      changed();
      return item;
    },
    async sendNow(key) {
      const outcome = await exclusive(async () => {
        const item = await storage.get<QueuedCapture>(Q + key);
        if (!item) return resolveGone(key);
        // Order within a set of books: older captures go first.
        for (const older of await list<QueuedCapture>(Q, item.orgId)) {
          if (older.key >= key) break;
          const o = await attempt(older.key, true);
          if (o.status === 'queued' || o.status === 'signed_out') {
            if (o.status === 'queued') await reschedule(item);
            return o;
          }
        }
        return attempt(key, false);
      });
      changed();
      return outcome;
    },
    async flush(flushOpts = {}) {
      await exclusive(async () => {
        // A capture waiting out its backoff holds back the newer ones of the same books (order), not other books'.
        const held = new Set<string>();
        for (const item of await list<QueuedCapture>(Q)) {
          if (held.has(item.orgId)) continue;
          if (!flushOpts.force && Date.parse(item.nextTryAt) > now().getTime()) {
            held.add(item.orgId);
            continue;
          }
          const outcome = await attempt(item.key, true);
          if (outcome.status === 'queued' || outcome.status === 'signed_out') break;
        }
      });
      changed();
    },
    queued: (orgId) => list<QueuedCapture>(Q, orgId),
    failed: (orgId) => list<FailedCapture>(F, orgId),
    sent: (orgId) => list<SentCapture>(S, orgId),
    retry: (key) => exclusive(async () => {
      const item = await storage.get<FailedCapture>(F + key);
      if (!item) return;
      const { error: _e, reason: _r, failedAt: _f, ...queued } = item;
      await storage.set(Q + key, { ...queued, nextTryAt: now().toISOString() });
      await storage.delete(F + key);
      changed();
    }),
    discard: (key) => exclusive(async () => {
      await storage.delete(F + key);
      await storage.delete(Q + key);
      changed();
    }),
    markSeen: (key) => exclusive(async () => {
      await storage.delete(S + key);
      changed();
    }),
    clearOrg: (orgId) => exclusive(async () => {
      for (const prefix of [Q, F, S]) {
        for (const k of await storage.keys(prefix)) {
          const item = await storage.get<{ orgId: string }>(k);
          if (item?.orgId === orgId) await storage.delete(k);
        }
      }
      changed();
    }),
    clearAll: () => exclusive(async () => {
      for (const prefix of [Q, F, S]) for (const k of await storage.keys(prefix)) await storage.delete(k);
      changed();
    }),
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
